/**
 * What the phone actually thinks, as opposed to what the CSS was written expecting.
 *
 * The add button sits above a safe-area inset, and every attempt to place it correctly from a
 * desktop has been an assumption about what iOS reports. This measures it instead: a probe
 * element is given each candidate expression in turn and asked where it landed. An expression
 * the browser cannot parse is dropped, which leaves the element at its static position, and the
 * measurement says so plainly rather than looking like a small number.
 */

export interface Reading {
  label: string;
  value: string;
}

/** The expressions `--floor` has been through, so their answers can be compared side by side. */
const FLOOR_CANDIDATES = [
  'env(safe-area-inset-bottom)',
  'env(safe-area-inset-bottom, 0px)',
  'calc(20px + env(safe-area-inset-bottom))',
  'max(12px, env(safe-area-inset-bottom, 0px))',
  'clamp(12px, env(safe-area-inset-bottom, 0px), 34px)',
  'var(--floor)',
];

export function collect(): Reading[] {
  return [
    ...bundle(),
    ...display(),
    ...viewport(),
    ...insets(),
    ...floors(),
    ...frame(),
    ...whereTheButtonIs(),
  ];
}

/** Which build is running, which is the first thing to establish before trusting anything else. */
function bundle(): Reading[] {
  const sources = [...document.querySelectorAll('script[src]')].map(
    (script) => script.getAttribute('src') ?? '',
  );

  return [{ label: 'bundle', value: sources.join(' ') || 'none' }];
}

function display(): Reading[] {
  const standalone = (navigator as Navigator & { standalone?: boolean }).standalone;

  return [
    {
      label: 'display-mode',
      value: window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser',
    },
    { label: 'navigator.standalone', value: String(standalone ?? 'undefined') },
  ];
}

function viewport(): Reading[] {
  const visual = window.visualViewport;

  return [
    { label: 'innerHeight', value: `${window.innerHeight}` },
    { label: 'clientHeight', value: `${document.documentElement.clientHeight}` },
    { label: 'screen.height', value: `${window.screen.height}` },
    { label: 'devicePixelRatio', value: `${window.devicePixelRatio}` },
    {
      label: 'visualViewport',
      value:
        visual === undefined || visual === null
          ? 'none'
          : `h=${Math.round(visual.height)} top=${Math.round(visual.offsetTop)} scale=${visual.scale}`,
    },
  ];
}

function insets(): Reading[] {
  return (['top', 'right', 'bottom', 'left'] as const).map((side) => ({
    label: `inset-${side}`,
    value: resolvedPadding(`env(safe-area-inset-${side})`),
  }));
}

function floors(): Reading[] {
  return FLOOR_CANDIDATES.map((expression) => ({
    label: expression,
    value: `${resolvedBottom(expression)}px up`,
  }));
}

/**
 * Where the app's own frame stops.
 *
 * The other half of the same question: the add button can only sit on the bottom edge if the
 * thing it is measured from reaches it. Both are reported against `innerHeight`, so a frame
 * that stops short says so as a positive number of pixels of nothing.
 */
function frame(): Reading[] {
  return (['body', '#app', '.app', 'main'] as const).map((selector) => {
    const element = document.querySelector(selector);

    return {
      label: `${selector} bottom`,
      value:
        element === null
          ? 'not drawn'
          : `${Math.round(window.innerHeight - element.getBoundingClientRect().bottom)}px up`,
    };
  });
}

let remembered: Reading[] = [{ label: '.add', value: 'not seen yet' }];

/**
 * Records where the add button landed.
 *
 * This screen does not have one — it belongs to the inventory — so the button reports itself as
 * it is drawn and the reading is read back here.
 */
export function noteAddButton(button: Element | null): void {
  if (button === null) {
    return;
  }

  const box = button.getBoundingClientRect();

  remembered = [
    {
      label: '.add',
      value: `${Math.round(window.innerHeight - box.bottom)}px up, ${Math.round(box.height)}px tall`,
    },
    { label: '.add bottom', value: getComputedStyle(button).bottom },
  ];
}

/** The button itself — the only measurement here that is not a stand-in. */
function whereTheButtonIs(): Reading[] {
  const button = document.querySelector('.add');
  if (button !== null) {
    noteAddButton(button);
  }

  return remembered;
}

/**
 * How far up a fixed element with this `bottom` actually lands.
 *
 * Measured rather than read back from `getComputedStyle`, because a dropped declaration still
 * computes to something — the static position — and the number alone would not admit it.
 */
function resolvedBottom(expression: string): number {
  return measure(`position:fixed;left:0;width:1px;height:1px;bottom:${expression}`, (probe) =>
    Math.round(window.innerHeight - probe.getBoundingClientRect().bottom),
  );
}

function resolvedPadding(expression: string): string {
  return measure(
    `position:fixed;left:0;width:1px;padding-bottom:${expression}`,
    (probe) => getComputedStyle(probe).paddingBottom,
  );
}

function measure<T>(style: string, read: (probe: HTMLElement) => T): T {
  const probe = document.createElement('div');
  probe.style.cssText = `${style};visibility:hidden;pointer-events:none`;
  document.body.append(probe);

  try {
    return read(probe);
  } finally {
    probe.remove();
  }
}

export function asText(readings: readonly Reading[]): string {
  return readings.map((reading) => `${reading.label}: ${reading.value}`).join('\n');
}
